/**
 * CustomizableDealsTable Component
 * 
 * REFACTORED FOR SUPABASE: Server-side filtering, sorting, and pagination.
 * 
 * This component now receives pre-filtered, pre-sorted, and pre-paginated data
 * from the parent component which uses the useDealsTable hook.
 * 
 * Key changes from Firebase version:
 * - No more client-side filtering (filteredDeals useMemo removed)
 * - No more client-side sorting (sortedDeals useMemo removed)
 * - No more "Load More" button - true pagination with page controls
 * - Column header clicks trigger server-side sort via callback
 */

import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  useColorModeValue,
  Icon,
  HStack,
  Select,
  IconButton,
  Button,
  VStack,
  Text,
  Spinner,
  Center,
  Badge,
} from '@chakra-ui/react';
import { FiArrowUp, FiArrowDown, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { ALL_COLUMNS, getVisibleColumns } from './tableColumnDefinitions';
import SelectAllBanner from './SelectAllBanner';
import { useAuth } from '../../contexts/AuthContext';

/**
 * CustomizableDealsTable - Display deals with server-side operations
 * 
 * Props changed for Supabase:
 * - deals: Pre-filtered, pre-sorted page of data from server
 * - totalCount: Total matching records (for pagination display)
 * - currentPage, totalPages: Pagination state from server
 * - onPageChange: Callback to change page (triggers server fetch)
 * - onSortChange: Callback to change sort (triggers server fetch)
 * - sortBy, sortDirection: Current sort state
 * - loading: Whether data is being fetched
 */
export default function CustomizableDealsTable({
  // Data (from server - already filtered, sorted, paginated)
  deals = [],
  totalCount = 0,
  
  // Pagination (server-side)
  currentPage = 1,
  totalPages = 1,
  pageSize = 25,
  onPageChange,
  onPageSizeChange,
  
  // Sorting (server-side)
  sortBy,
  sortDirection,
  onSortChange,
  
  // Status
  loading = false,
  
  // Selection
  selectedDeals = [],
  onSelectionChange,
  
  // Select All functionality
  onSelectAllMatchingFilters, // Callback to select all items matching filters
  isAllMatchingFiltersSelected = false, // Whether all matching items are selected
  isSelectAllLoading = false, // Whether fetchAllIds is in progress
  onClearAllSelection, // Callback to clear all selections
  
  // Columns
  visibleColumns,
  columnOrder,
  
  // Callbacks
  onDealClick,
  
  // Users lookup map for Owner column
  usersMap,

  // When set (non-admin viewers with book shares), rows owned by someone else
  // get a small owner chip next to the name so shared records are obvious.
  ownerChipViewerId = null,
}) {
  const history = useHistory();
  
  // Theme colors
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const tableHeaderBG = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const paginationBG = useColorModeValue('gray.50', 'gray.800');

  const { SuperAdmin, Roles } = useAuth();
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');

  // Get visible column definitions in order. Admin-only columns (e.g. record
  // scope) are stripped for non-admins even if a saved preference includes them.
  const orderedColumns = React.useMemo(() => {
    return getVisibleColumns(columnOrder || visibleColumns || [])
      .filter(c => !c.adminOnly || isCRMAdmin);
  }, [columnOrder, visibleColumns, isCRMAdmin]);

  // Handle column header click for sorting
  const handleColumnSort = (columnId) => {
    const column = ALL_COLUMNS.find(c => c.id === columnId);
    if (!column || !column.sortable) return;

    if (sortBy === columnId) {
      // Toggle direction
      onSortChange(columnId, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to asc
      onSortChange(columnId, 'asc');
    }
  };

  // Handle row selection
  const handleSelectAll = () => {
    const allCurrentPageIds = deals.map(d => d.id);
    const allSelected = allCurrentPageIds.every(id => selectedDeals.includes(id));
    
    if (allSelected) {
      // If "all matching filters" mode is active, clear everything
      if (isAllMatchingFiltersSelected && onClearAllSelection) {
        onClearAllSelection();
      } else {
        // Deselect all on this page
        allCurrentPageIds.forEach(id => onSelectionChange(id, false));
      }
    } else {
      // Select all on this page
      allCurrentPageIds.forEach(id => {
        if (!selectedDeals.includes(id)) {
          onSelectionChange(id, true);
        }
      });
    }
  };

  // Navigate to deal page
  const handleWorkClick = (deal, e) => {
    e.stopPropagation();
    history.push(`/crm/deals/${deal.id}`);
  };

  // Render column header with sort indicator
  const renderColumnHeader = (column) => {
    const isSorted = sortBy === column.id;
    
    return (
      <Th key={column.id} width={column.width} fontSize="xs" py={2}>
        <HStack spacing={1} justify="space-between">
          <HStack
            spacing={1}
            cursor={column.sortable ? 'pointer' : 'default'}
            onClick={() => column.sortable && handleColumnSort(column.id)}
            _hover={column.sortable ? { opacity: 0.7 } : {}}
          >
            <Text fontSize="xs" fontWeight="semibold">{column.label}</Text>
            {column.sortable && isSorted && (
              <Icon
                as={sortDirection === 'asc' ? FiArrowUp : FiArrowDown}
                boxSize={3}
              />
            )}
          </HStack>
        </HStack>
      </Th>
    );
  };

  // Calculate pagination display values
  const startRecord = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalCount);
  
  // Check if all items on current page are selected
  const allPageItemsSelected = deals.length > 0 && deals.every(d => selectedDeals.includes(d.id));

  return (
    <VStack align="stretch" spacing={0}>
      {/* Select All Banner */}
      <SelectAllBanner
        selectedCount={selectedDeals.length}
        pageCount={deals.length}
        totalCount={totalCount}
        entityName="deals"
        onSelectAll={onSelectAllMatchingFilters}
        onClearSelection={onClearAllSelection}
        isAllSelected={isAllMatchingFiltersSelected}
        isLoading={isSelectAllLoading}
      />
      
      {/* Table Container */}
      <Box 
        overflowX="auto" 
        overflowY="auto" 
        maxH="calc(100vh - 280px)" 
        borderWidth="1px" 
        borderRadius="lg" 
        borderColor={borderColor} 
        borderBottomRadius={0}
        position="relative"
      >
        {/* Loading overlay */}
        {loading && (
          <Center
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="whiteAlpha.700"
            zIndex={10}
          >
            <Spinner size="lg" color="blue.500" />
          </Center>
        )}
        
        <Table variant="simple" size="sm">
          <Thead bg={tableHeaderBG} position="sticky" top={0} zIndex={1}>
            <Tr>
              {/* Selection checkbox */}
              <Th width="50px" fontSize="xs" py={2}>
                <Checkbox
                  size="sm"
                  isChecked={
                    deals.length > 0 &&
                    deals.every(d => selectedDeals.includes(d.id))
                  }
                  isIndeterminate={
                    deals.some(d => selectedDeals.includes(d.id)) &&
                    !deals.every(d => selectedDeals.includes(d.id))
                  }
                  onChange={handleSelectAll}
                />
              </Th>
              
              {/* Column headers */}
              {orderedColumns.map(column => renderColumnHeader(column))}
              
              {/* Actions column */}
              <Th width="100px" fontSize="xs" py={2}>Actions</Th>
            </Tr>
          </Thead>
          
          <Tbody>
            {deals.length === 0 && !loading ? (
              <Tr>
                <Td colSpan={orderedColumns.length + 2}>
                  <Box textAlign="center" py={8}>
                    <Text color="gray.500" fontSize="sm">
                      No deals found matching your criteria
                    </Text>
                  </Box>
                </Td>
              </Tr>
            ) : (
              deals.map((deal) => (
                <Tr 
                  key={deal.id} 
                  _hover={{ bg: hoverBg }} 
                  fontSize="xs"
                  opacity={loading ? 0.5 : 1}
                >
                  {/* Selection checkbox */}
                  <Td onClick={(e) => e.stopPropagation()} py={2}>
                    <Checkbox
                      size="sm"
                      isChecked={selectedDeals.includes(deal.id)}
                      onChange={() => onSelectionChange(deal.id)}
                    />
                  </Td>
                  
                  {/* Data cells */}
                  {orderedColumns.map(column => (
                    <Td key={column.id} fontSize="xs" py={2}>
                      {column.renderCell 
                        ? column.renderCell(deal, onDealClick, usersMap) 
                        : column.accessor(deal)}
                      {column.id === 'name' && ownerChipViewerId && deal.ownerId
                        && deal.ownerId !== ownerChipViewerId && usersMap?.get(deal.ownerId) && (
                        <Badge colorScheme="teal" variant="subtle" fontSize="2xs" ml={1} verticalAlign="middle">
                          {usersMap.get(deal.ownerId)}
                        </Badge>
                      )}
                    </Td>
                  ))}
                  
                  {/* Actions cell */}
                  <Td py={2}>
                    <Button
                      size="xs"
                      colorScheme="blue"
                      onClick={(e) => handleWorkClick(deal, e)}
                    >
                      Work
                    </Button>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Pagination Controls */}
      <HStack 
        justify="space-between" 
        px={4} 
        py={3} 
        bg={paginationBG} 
        borderWidth="1px" 
        borderTopWidth={0}
        borderColor={borderColor}
        borderBottomRadius="lg"
        fontSize="xs"
      >
        {/* Page size selector */}
        <HStack spacing={2}>
          <Text fontWeight="medium">Rows per page:</Text>
          <Select
            size="xs"
            value={pageSize}
            onChange={(e) => onPageSizeChange && onPageSizeChange(Number(e.target.value))}
            maxW="80px"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Select>
        </HStack>
        
        {/* Record count */}
        <HStack spacing={2}>
          <Text color="gray.600">
            {startRecord}-{endRecord} of {totalCount.toLocaleString()}
          </Text>
        </HStack>
        
        {/* Page navigation */}
        <HStack spacing={1}>
          <IconButton
            icon={<FiChevronsLeft />}
            size="xs"
            variant="ghost"
            aria-label="First page"
            onClick={() => onPageChange && onPageChange(1)}
            isDisabled={currentPage === 1 || loading}
          />
          <IconButton
            icon={<FiChevronLeft />}
            size="xs"
            variant="ghost"
            aria-label="Previous page"
            onClick={() => onPageChange && onPageChange(currentPage - 1)}
            isDisabled={currentPage === 1 || loading}
          />
          <Text fontWeight="medium" px={2}>
            Page {currentPage} of {totalPages}
          </Text>
          <IconButton
            icon={<FiChevronRight />}
            size="xs"
            variant="ghost"
            aria-label="Next page"
            onClick={() => onPageChange && onPageChange(currentPage + 1)}
            isDisabled={currentPage === totalPages || loading}
          />
          <IconButton
            icon={<FiChevronsRight />}
            size="xs"
            variant="ghost"
            aria-label="Last page"
            onClick={() => onPageChange && onPageChange(totalPages)}
            isDisabled={currentPage === totalPages || loading}
          />
        </HStack>
      </HStack>
    </VStack>
  );
}
